import { createCacheableJsonResponse } from '@tools/server/CacheHeaderResponse';
import ShopwareClient from '@connectors/newport/ShopwareClient';
import { ShopwareDeliveryTime, ShopwareUuid } from '@services/types/Shopware';
import { NextApiRequest, NextApiResponse } from 'next';

export type DeliveryTimesMappedResponse = Record<ShopwareUuid, string>;
export type DeliveryTimeAlternativeName = 'plp' | 'pdp';
export type MarketingLabel =  { translated: { name: any; }; name: any; id: string; };

export type StorefrontDeliveryTime = {
    id: ShopwareUuid,
    name: string,
    indicator: 'black' | 'in-stock' | 'out-of-stock' | 'incoming' | null;
    alternativeNames: Record<DeliveryTimeAlternativeName, string>,
};

export interface StaticLabelsResponse {
    status: 'success',
    marketingLabels: Record<ShopwareUuid, string>,
    inPhysicalStores: Record<ShopwareUuid, string>,
    deliveryTimes: {
        mapped: DeliveryTimesMappedResponse,
        list: StorefrontDeliveryTime[],
    }
}

interface StoreLocation {
    storeId: string;
    propertyId: string;
    code: string;
    name: string;
    address: string;
    phone: string;
    email: string;
    openingHours: string;
}
export interface PhysicalStores {
    [key: string]: StoreLocation;
}

function getSafeTranslatedFields(object: any, key: string) {
    return object?.translated?.[key] || object?.[key];
}

export function resolveIndicator(deliveryTime: ShopwareDeliveryTime) {
    if (deliveryTime.unit === 'day' && deliveryTime.max <= 14) {
        return 'in-stock';
    }

    if (deliveryTime.unit === 'week' && deliveryTime.max <= 3) {
        return 'in-stock';
    }

    if (deliveryTime.unit === 'week') {
        return 'incoming';
    }

    if (deliveryTime.unit === 'month') {
        return 'black';
    }

    if (deliveryTime.unit === 'year' && deliveryTime.max <= 1) {
        return 'black';
    }

    return 'out-of-stock';
}

export default async function handler(
    req: NextApiRequest,
    res: NextApiResponse<StaticLabelsResponse | { status: 'error'; data: any }>,
) {
    const client = new ShopwareClient(req);
    const response = await client.getStaticData();

    if (!response?.properties) {
        return res.send({ status: 'error', data: response?.error });
    }

    const marketingLabels: StaticLabelsResponse['marketingLabels'] = {};
    response?.properties?.marketing_label?.forEach((label: MarketingLabel) => {
        const result = (label?.translated?.name || label?.name);
        if (result?.length) {
            marketingLabels[label.id] = result;
        }
    });

    const inPhysicalStores: StaticLabelsResponse['inPhysicalStores'] = {};
    response?.properties?.in_physical_store?.forEach((label: MarketingLabel) => {
        const result = (label?.translated?.name || label?.name);
        if (result?.length) {
            inPhysicalStores[label.id] = result;
        }
    });

    const deliveryTimesMap: DeliveryTimesMappedResponse = {};
    const deliveryTimes: StaticLabelsResponse['deliveryTimes']['list'] = [];
    response.deliveryTimes.forEach((deliveryTime: ShopwareDeliveryTime) => {
        const result = getSafeTranslatedFields(deliveryTime, 'name');

        if (result?.length) {
            deliveryTimesMap[deliveryTime.id] = result;
        }

        function customFields(key: string) {
            // @ts-ignore
            return deliveryTime?.translated?.customFields?.[key] || deliveryTime?.customFields?.[key] || result;
        }

        deliveryTimes.push({
            id: deliveryTime.id,
            name: result,
            indicator: resolveIndicator(deliveryTime),
            alternativeNames: {
                plp: customFields('delivery_time_texts_main_PLP') || null,
                pdp: customFields('delivery_time_texts_main_PDP') || null,
            },
        });
    });

    return createCacheableJsonResponse(req, res, {
        status: 'success',
        marketingLabels,
        inPhysicalStores,
        deliveryTimes: {
            mapped: deliveryTimesMap,
            list: deliveryTimes,
        },
    });
}
